<script setup lang="ts">
import type { TabsListProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { provide } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { TabsList } from 'reka-ui'
import { cn } from '@ds/lib/utils'
import type { TabsVariant } from '.'

type Props = TabsListProps & {
  class?: HTMLAttributes['class']
  variant?: TabsVariant
}

/*
 * `withDefaults`, not a destructured default: `reactiveOmit` below calls
 * `toRefs` internally, which requires a genuinely reactive props object —
 * the `{ x = default, ...rest }` destructure macro's rest object fails that
 * check and `toRefs` warns "expects a reactive object but received a plain
 * one", confirmed empirically.
 */
const props = withDefaults(defineProps<Props>(), {
  variant: 'underline',
})

const delegatedProps = reactiveOmit(props, 'class', 'variant')

// Read by each `TabsTrigger` — see the type's own doc comment.
provide('tabsVariant', props.variant)
</script>

<template>
  <TabsList
    v-bind="delegatedProps"
    :class="cn(
      'inline-flex items-stretch gap-1',
      variant === 'pill' ? 'bg-muted p-1 rounded-ds-md' : 'border-b border-border',
      props.class,
    )"
  >
    <slot />
  </TabsList>
</template>
